/**
 * The location page's Devices section, and its due-date cells.
 *
 * Lifted verbatim out of `sites/trade-panel.tsx` (#1011) as part of the
 * pure-move commit: no behaviour changed here, so the diff is a rename
 * and the only question a reviewer has to answer is "did anything
 * change?". The IA flip that consumes it is the next commit.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ReconcileFromListDialog } from "../reconcile-from-list";
import { reconcileEntryVisible } from "../location-page-v2";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { DataTable, useSortedRows, type Column } from "@/components/ui/data-table";
import { cn, formatDate } from "@/lib/utils";
import { dateSortValue } from "@/lib/table-sort";
import { HardDrive, AlertCircle, ChevronRight } from "lucide-react";
import { ShowMoreFooter } from "./section-chrome";
import {
    ASSEMBLY_LABEL,
    ASSET_KIND_GROUP_LABEL,
    DEFAULT_VISIBLE_ROWS,
    SERVICE_LABEL,
    type DeviceRow,
    type SiteContext,
    type TradeSummary,
} from "./types";

/**
 * `trade` is a FILTER now, not the identity of the tab this lives in.
 * `null` means "All", and the query then omits `?trade=` entirely rather
 * than sending `trade=all` -- the server would look for a trade with that
 * code and return nothing.
 *
 * Omitting it is also the only way the untagged legacy devices survive.
 * `assetMatchesRequestedTradeSql` grandfathers trade-less imported rows
 * into a family by `asset_type`, so filtering client-side on
 * `row.trade_code === "backflow"` would silently drop every device the
 * ServiceTitan mapper ever wrote -- "Devices (0)" over a real device on
 * site, which docs/bible/sites.md records as "feels broken". So the chip
 * sets the query param and the server decides.
 */
export function DevicesSection({
    site,
    trade,
    summary,
}: {
    site: SiteContext;
    trade: string | null;
    summary: TradeSummary | null;
}) {
    const [search, setSearch] = useState("");
    const [expanded, setExpanded] = useState(false);
    const [reconcileOpen, setReconcileOpen] = useState(false);

    const q = useQuery({
        queryKey: ["site-trade-assets", site.siteId, trade, search],
        queryFn: () => {
            const p = new URLSearchParams();
            p.set("limit", "500");
            if (trade) p.set("trade", trade);
            if (search.trim()) p.set("q", search.trim());
            return api.get<{ assets: DeviceRow[]; total: number }>(`/api/sites/${site.siteId}/assets?${p.toString()}`);
        },
        enabled: !!site.siteId,
    });

    const rows = q.data?.assets ?? [];
    const total = q.data?.total ?? 0;
    const columns: Column<DeviceRow>[] = [
        {
            key: "name",
            header: "Device",
            sortable: true,
            render: (r) => (
                <Link to={`/assets/${r.id}`} className="hover:underline">
                    <div className="font-medium flex items-center gap-1.5 flex-wrap">
                        {r.name}
                        {r.bypass && (
                            // PRE-CUTOVER-UX-FEEDBACK item #7: a
                            // DCDA/RPDA main + its bypass render as
                            // one row in the office device list. The
                            // bypass identity (size/make/model/SN)
                            // lives in `r.bypass`; this pill flags
                            // its presence and acts as a visual
                            // "Main + Bypass" affordance.
                            <span
                                className="rounded-full bg-primary/10 text-primary text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5"
                                title={[
                                    "Linked bypass:",
                                    r.bypass.size_label,
                                    r.bypass.manufacturer,
                                    r.bypass.model,
                                    r.bypass.serial_number ? `S/N ${r.bypass.serial_number}` : null,
                                ]
                                    .filter(Boolean)
                                    .join(" ")}
                            >
                                {r.bypass.serial_number ? `Bypass: ${r.bypass.serial_number}` : "+ Bypass"}
                            </span>
                        )}
                        {r.needs_verification_reason && (
                            <Badge
                                variant="outline"
                                className="text-[10px] border-amber-500/40 text-amber-600 bg-amber-500/10 inline-flex items-center gap-1"
                                title={r.needs_verification_reason}
                            >
                                <AlertCircle className="h-3 w-3" /> Verify on site
                            </Badge>
                        )}
                    </div>
                    <div className="text-xs text-foreground/80">
                        {[
                            r.size_label,
                            r.assembly_type ? (ASSEMBLY_LABEL[r.assembly_type] ?? r.assembly_type.toUpperCase()) : null,
                        ]
                            .filter(Boolean)
                            .join(" · ") || "—"}
                    </div>
                </Link>
            ),
        },
        {
            key: "serial_number",
            header: "Serial #",
            sortable: true,
            render: (r) => (
                <div className="space-y-0.5">
                    <span className="font-mono text-xs">{r.serial_number ?? "—"}</span>
                    {r.bypass?.serial_number && (
                        <div className="font-mono text-[10px] text-foreground/70" title="Bypass serial">
                            {r.bypass.serial_number}
                        </div>
                    )}
                </div>
            ),
        },
        {
            key: "make_model",
            header: "Make / Model",
            sortable: true,
            sortAccessor: (r) => [r.manufacturer, r.model].filter(Boolean).join(" ") || null,
            render: (r) =>
                [r.manufacturer, r.model].filter(Boolean).join(" · ") || <span className="text-foreground/80">—</span>,
        },
        // Only in the All view: with a trade filter active every row is that
        // trade, and a column repeating it is noise.
        ...(trade
            ? []
            : [
                  {
                      key: "effective_trade_code",
                      header: "Trade",
                      sortable: true,
                      sortAccessor: (r: DeviceRow) => r.effective_trade_name,
                      render: (r: DeviceRow) =>
                          r.effective_trade_name ? (
                              <span className="text-sm">
                                  {r.effective_trade_name}
                                  {r.trade_is_inferred && (
                                      <span
                                          className="ml-1 text-xs text-foreground/70"
                                          title="Imported without a trade tag. GWC placed it by device type — it counts and it filters."
                                      >
                                          (untagged)
                                      </span>
                                  )}
                              </span>
                          ) : (
                              <span className="text-foreground/80">—</span>
                          ),
                  } satisfies Column<DeviceRow>,
              ]),
        {
            key: "service_type",
            header: trade === "backflow" ? "Serves" : "Type",
            sortable: true,
            sortAccessor: (r) =>
                r.service_type ? (SERVICE_LABEL[r.service_type] ?? r.service_type) : r.service_type_other,
            render: (r) => {
                const v = r.service_type
                    ? (SERVICE_LABEL[r.service_type] ?? r.service_type)
                    : (r.service_type_other ?? null);
                return v ? <span className="text-sm">{v}</span> : <span className="text-foreground/80">—</span>;
            },
        },
        {
            // #819 B10 M9: the authoritative "where is it" finally SHOWS in
            // the table whose own search box advertises location — two
            // disagreeing records used to require opening each device.
            key: "location_description",
            header: "Location",
            sortable: true,
            render: (r) =>
                r.location_description ? (
                    <span className="text-sm" title={r.location_description}>
                        {r.location_description}
                    </span>
                ) : (
                    <span
                        className="text-foreground/80"
                        title="No location on property recorded — the tech has nothing to find the device by"
                    >
                        —
                    </span>
                ),
        },
        {
            key: "next_due_date",
            header: "Next due",
            sortable: true,
            sortAccessor: (r) => dateSortValue(r.next_due_date),
            render: (r) => <DueCell assetId={r.id} date={r.next_due_date} siteId={site.siteId} />,
        },
        {
            key: "go",
            header: "",
            render: (r) => (
                <Link
                    to={`/assets/${r.id}`}
                    className={cn(buttonVariants({ variant: "ghost", size: "sm" }), "h-7 px-2")}
                >
                    <ChevronRight className="h-4 w-4" />
                </Link>
            ),
        },
    ];

    // Sort every device, THEN cut to the preview — so "sort by Next due"
    // puts the most overdue device first even when it was row 40.
    const { sorted, controlledSort } = useSortedRows(rows, columns, { sortId: "site.devices" });
    const visibleRows = expanded ? sorted : sorted.slice(0, DEFAULT_VISIBLE_ROWS);

    // Group by asset type so mixed-family trades (fire_ext: extinguishers +
    // e-lights) read as distinct sections instead of one interleaved table.
    // Single-family trades (backflow) render exactly as before.
    const assetTypeGroups = useMemo(() => {
        const map = new Map<string, DeviceRow[]>();
        for (const r of visibleRows) {
            const key = (r.asset_kind ?? "other").toLowerCase();
            const list = map.get(key);
            if (list) list.push(r);
            else map.set(key, [r]);
        }
        return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
    }, [visibleRows]);

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                    <HardDrive className="h-4 w-4" /> Devices ({total})
                </CardTitle>
                {/* Was gated on `tradeCode === "backflow"`, which with trade
                    as a filter would hide Reconcile in the All view -- the
                    default. docs/bible/sites.md records "Reconcile from list
                    stays visible at zero devices" as a do-not-regress rule,
                    and it has three entry points; they all go through the one
                    helper now so the doctrine has a single owner. */}
                {reconcileEntryVisible(total) && (
                    <Button variant="outline" size="sm" onClick={() => setReconcileOpen(true)}>
                        Reconcile from list
                    </Button>
                )}
            </CardHeader>
            <CardContent>
                <div className="mb-3">
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search serial, model, location…"
                        className="max-w-md"
                    />
                </div>
                {assetTypeGroups.length > 1 ? (
                    <div className="space-y-4">
                        {assetTypeGroups.map(([kind, groupRows]) => (
                            <div key={kind}>
                                <div className="text-xs font-bold uppercase tracking-wide text-foreground/60 mb-1.5">
                                    {ASSET_KIND_GROUP_LABEL[kind] ?? kind} ({groupRows.length})
                                </div>
                                <DataTable
                                    columns={columns}
                                    data={groupRows}
                                    keyField="id"
                                    loading={false}
                                    controlledSort={controlledSort}
                                />
                            </div>
                        ))}
                    </div>
                ) : (
                    <DataTable
                        columns={columns}
                        data={visibleRows}
                        keyField="id"
                        controlledSort={controlledSort}
                        loading={q.isLoading}
                        emptyMessage={
                            search
                                ? "No devices match your search."
                                : summary
                                  ? `No ${summary.name.toLowerCase()} devices yet at this location.`
                                  : "No devices on record at this location yet."
                        }
                    />
                )}
                <ShowMoreFooter total={total} expanded={expanded} onToggle={() => setExpanded((v) => !v)} />
            </CardContent>
            {reconcileOpen && (
                <ReconcileFromListDialog
                    siteId={site.siteId}
                    open={reconcileOpen}
                    onClose={() => setReconcileOpen(false)}
                />
            )}
        </Card>
    );
}

function DueChip({ date }: { date: string }) {
    const dueMs = new Date(date).getTime();
    const now = Date.now();
    const daysOut = Math.floor((dueMs - now) / (1000 * 60 * 60 * 24));
    const overdue = daysOut < 0;
    const soon = daysOut >= 0 && daysOut <= 90;
    return (
        <Badge
            variant="outline"
            className={cn(
                "text-[11px] tabular-nums",
                overdue && "border-destructive/40 text-destructive bg-destructive/10",
                soon && !overdue && "border-amber-500/40 text-amber-600 bg-amber-500/10",
            )}
        >
            {formatDate(date)}
        </Badge>
    );
}

// Inline-editable due date for one device row. A NULL due date is an
// invisible compliance gap (it never shows in the test-due queue), so we
// surface it with a warning badge and let staff backfill it in place.
function DueCell({ assetId, date, siteId }: { assetId: string; date: string | null; siteId: string }) {
    const qc = useQueryClient();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(date ?? "");

    const save = useMutation({
        mutationFn: (value: string | null) => api.patch(`/api/assets/${assetId}`, { nextDueDate: value }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["site-trade-assets", siteId] });
            toast.success("Due date updated");
            setEditing(false);
        },
        onError: () => toast.error("Could not update due date"),
    });

    if (editing) {
        return (
            <div className="flex flex-wrap items-center gap-1">
                <input
                    type="date"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    className="h-7 rounded-md border border-input bg-background px-1.5 text-xs"
                />
                <Button
                    size="sm"
                    className="h-7 px-2 text-xs"
                    disabled={save.isPending}
                    onClick={() => save.mutate(draft || null)}
                >
                    Save
                </Button>
                <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs"
                    disabled={save.isPending}
                    onClick={() => setEditing(false)}
                >
                    Cancel
                </Button>
            </div>
        );
    }

    return (
        <button
            type="button"
            className="group inline-flex items-center gap-1"
            title="Edit due date"
            onClick={() => {
                setDraft(date ?? "");
                setEditing(true);
            }}
        >
            {date ? (
                <DueChip date={date} />
            ) : (
                <Badge
                    variant="outline"
                    className="text-[11px] border-amber-500/40 text-amber-600 bg-amber-500/10 inline-flex items-center gap-1"
                >
                    <AlertCircle className="h-3 w-3" /> No due date
                </Badge>
            )}
            <span className="text-[10px] text-primary opacity-0 group-hover:opacity-100">Edit</span>
        </button>
    );
}

// ── Tests / Inspections ──────────────────────────────────────────────
